import React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { Box, Chip, Typography, FormHelperText } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 300,
      borderRadius: 3,
      boxShadow: "0 12px 40px rgba(102, 126, 234, 0.15)",
      background:
        "linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95))",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(102, 126, 234, 0.1)",
      marginTop: 1,
      fontFamily: "Host Grotesk, sans-serif",
    },
  },
};

export const PLATFORMS = [
  "twitter",
  "facebook",
  "instagram",
  "youtube",
  "threads",
  "reddit",
];

export default function PlatformsSelect({
  value = [],
  onChange,
  disabled = false,
  error = false,
}) {
  const handleChange = (event) => {
    const next = event.target.value;

    // User clicked "All Platforms" while it wasn't selected → collapse to ["all"]
    if (!value.includes("all") && next.includes("all")) {
      onChange(["all"]);
      return;
    }

    // "All" was selected and user clicked a specific platform → drop "all", keep specific
    if (value.includes("all") && next.length > 1) {
      const specific = next.filter((v) => v !== "all");
      if (specific.length > 0) { onChange(specific); }
      return;
    }

    // Guard: don't allow empty selection (covers deselecting the last item or "all" alone)
    const filtered = next.filter((v) => v !== "all");
    if (filtered.length === 0) return;

    onChange(filtered);
  };

  return (
    <FormControl
      fullWidth
      error={error}
      sx={{
        fontFamily: "Host Grotesk, sans-serif",
        minWidth: 200,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          background: "rgba(255, 255, 255, 0.9)",
          minHeight: 48,
          fontFamily: "Host Grotesk, sans-serif",
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#667eea",
            },
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#667eea",
              borderWidth: 2,
            },
          },
        },
        "& .MuiInputLabel-root": {
          color: "#64748b",
          fontWeight: 500,
          fontFamily: "Host Grotesk, sans-serif",
          "&.Mui-focused": {
            color: "#667eea",
          },
        },
        "& .MuiSelect-select": {
          padding: "14px 16px",
          fontFamily: "Host Grotesk, sans-serif",
        },
      }}
    >
      <InputLabel id="platforms-select-label">Select Platforms</InputLabel>
      <Select
        labelId="platforms-select-label"
        id="platforms-select"
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label="Select Platforms" />}
        renderValue={(selected) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              maxWidth: "100%",
            }}
          >
            {selected.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#94a3b8",
                  fontStyle: "italic",
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Select platforms...
              </Typography>
            ) : selected.includes("all") ? (
              <Chip
                label="All Platforms"
                size="small"
                sx={{
                  background: "rgba(102, 126, 234, 0.12)",
                  color: "#667eea",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  fontFamily: "Host Grotesk, sans-serif",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                  backdropFilter: "blur(8px)",
                }}
              />
            ) : selected.length > 3 ? (
              <Chip
                label={`${selected.length} platforms selected`}
                size="small"
                sx={{
                  background: "rgba(102, 126, 234, 0.12)",
                  color: "#667eea",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  fontFamily: "Host Grotesk, sans-serif",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                  backdropFilter: "blur(8px)",
                }}
              />
            ) : (
              selected.map((platform) => (
                <Chip
                  key={platform}
                  label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                  size="small"
                  sx={{
                    background: "rgba(102, 126, 234, 0.12)",
                    color: "#667eea",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    fontFamily: "Host Grotesk, sans-serif",
                    maxWidth: 120,
                    border: "1px solid rgba(102, 126, 234, 0.2)",
                    backdropFilter: "blur(8px)",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontFamily: "Host Grotesk, sans-serif",
                    },
                  }}
                />
              ))
            )}
          </Box>
        )}
        MenuProps={MenuProps}
        disabled={disabled}
      >
        {/* All option */}
        <MenuItem
          value="all"
          sx={{
            padding: "12px 16px",
            borderRadius: "8px",
            margin: "2px 8px",
            transition: "all 0.2s ease",
            borderBottom: "1px solid rgba(102, 126, 234, 0.1)",
            marginBottom: "8px",
            fontFamily: "Host Grotesk, sans-serif",
            "&:hover": {
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
              transform: "translateX(4px)",
            },
            "&.Mui-selected": {
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))",
              boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))",
              },
            },
          }}
        >
          <Checkbox
            checked={value.includes("all")}
            sx={{
              color: "rgba(102, 126, 234, 0.6)",
              "&.Mui-checked": {
                color: "#667eea",
              },
            }}
          />
          <ListItemText
            primary="All Platforms"
            sx={{
              marginLeft: 1.5,
              "& .MuiListItemText-primary": {
                fontWeight: value.includes("all") ? 700 : 600,
                color: value.includes("all") ? "#1f2937" : "#475569",
                fontSize: "0.875rem",
                fontFamily: "Host Grotesk, sans-serif",
              },
            }}
          />
        </MenuItem>

        {/* Individual platforms */}
        {PLATFORMS.map((platform) => (
          <MenuItem
            key={platform}
            value={platform}
            sx={{
              padding: "12px 16px",
              borderRadius: "8px",
              margin: "2px 8px",
              transition: "all 0.2s ease",
              fontFamily: "Host Grotesk, sans-serif",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                transform: "translateX(4px)",
              },
              "&.Mui-selected": {
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))",
                },
              },
            }}
          >
            <Checkbox
              checked={value.includes(platform) && !value.includes("all")}
              sx={{
                color: "rgba(102, 126, 234, 0.6)",
                "&.Mui-checked": {
                  color: "#667eea",
                },
              }}
            />
            <ListItemText
              primary={platform.charAt(0).toUpperCase() + platform.slice(1)}
              sx={{
                marginLeft: 1.5,
                "& .MuiListItemText-primary": {
                  fontWeight:
                    value.includes(platform) && !value.includes("all")
                      ? 600
                      : 500,
                  color:
                    value.includes(platform) && !value.includes("all")
                      ? "#1f2937"
                      : "#475569",
                  fontSize: "0.875rem",
                  fontFamily: "Host Grotesk, sans-serif",
                },
              }}
            />
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText>At least one platform must be selected.</FormHelperText>
      )}
    </FormControl>
  );
}
